/**
 * Quiz — "Find Your Ritual"
 * -----------------------------------------------------------------------------
 * STANDALONE script. This file is NOT a webpack build output — it is
 * hand-authored and safe to edit directly, following the same pattern as
 * assets/croev-quiz-v1.js. Deliberately kept out of dev/src/js/sections/
 * (which webpack auto-globs) so that adding this quiz never forces a rebuild
 * of vendor.js / common.js / theme.js. See CLAUDE.md "Webpack JS bundle drift".
 *
 * Paired with: sections/quiz-ritual.liquid, assets/quiz-ritual.css
 *
 * Responsibilities (question flow + email capture — the results screen is a
 * separate build):
 *   - one visible step at a time, driven off data attributes in the markup
 *   - single-select  -> mark, persist, auto-advance
 *   - multi-select   -> toggle up to data-max, "none of these" is mutually
 *                       exclusive, commit via next()
 *   - progress bar + "n/8" counter stay in sync with the step number
 *   - answers persist to sessionStorage so a refresh resumes in place
 *   - device/browser Back steps back through the quiz instead of leaving it
 *   - Q1's review carousel (separate ReviewsSlider, reader-driven only, shares
 *     nothing with the step engine beyond living in the same file)
 *   - the email screen: validation, consent gate, and the Klaviyo subscribe +
 *     event pair that records the lead and every answer
 *   - the loading interstitial: cycles its three photos, then hands off to the
 *     results (skipped entirely when there is no results destination)
 *
 * Public surface (for the results step, wired later):
 *   window.FroyaRitualQuiz.getAnswers()   -> { age: 'under_30', ... }
 *   window.FroyaRitualQuiz.getIdentity()  -> { first_name, email, ... }
 *   window.FroyaRitualQuiz.isComplete()   -> bool
 *   document  'quiz-ritual:answer'        -> { questionId, value, answers }
 *   document  'quiz-ritual:step'          -> { index, stepNumber, total }
 *   document  'quiz-ritual:complete'      -> { answers }
 *   document  'quiz-ritual:email'         -> { identity, answers }
 */
(function () {
  'use strict';

  var AUTO_ADVANCE_DELAY = 300; // matches the existing quiz funnel
  var STORAGE_VERSION = 1;

  /* ---------------------------------------------------------------- klaviyo -- */
  /**
   * Client-side Subscribe API — no klaviyo.js dependency, no backend. The Public
   * API Key (company_id) and the List ID come from the section settings and are
   * read off the section root's data-* attributes; blank either one and the
   * quiz simply stops reporting.
   *
   * Same shape as the /pages/quiz funnel (dev/src/js/sections/section-quiz-funnel.js)
   * on purpose — it is proven in production, and keeping the two identical means
   * one mechanism to reason about.
   *
   * What is deliberately NOT shared is the NAMING. Every metric, property and
   * source string is prefixed with the quiz identifier from the section settings
   * ("Find Your Ritual"), so this quiz's answers can never be confused with, or
   * silently overwrite, the older funnel's "Quiz *" properties on the same
   * profile. A visitor can take both quizzes and both records survive.
   */
  var KLAVIYO_REVISION = '2024-10-15';
  var DEFAULT_QUIZ_NAME = 'Find Your Ritual';

  // Every answer is sent to Klaviyo TWICE: the raw key (stable — the on-page
  // copy can be reworded without breaking an existing segment) and a human
  // label (for email templates via {{ person|lookup:'… Label' }}).
  // KEEP IN SYNC with the data-value attributes in sections/quiz-ritual.liquid.
  var ANSWER_LABELS = {
    age: {
      under_30: 'Under 30',
      '30_39': '30-39',
      '40_49': '40-49',
      '50_59': '50-59',
      '60_plus': '60+'
    },
    main_concern: {
      wrinkles_aging: 'Wrinkles & aging skin',
      dark_circles: 'Dark circles or under-eye puffiness',
      dull_tone: 'Dull, uneven skin tone',
      acne_breakouts: 'Acne & breakouts',
      rosacea_redness: 'Rosacea & redness',
      eczema_dry: 'Eczema, psoriasis, or dry/irritated skin'
    },
    secondary_concerns: {
      dull_tone: 'Dull, uneven tone',
      dark_circles: 'Dark circles & puffiness',
      dryness_rough: 'Dryness & rough patches',
      loss_firmness: 'Loss of firmness',
      breakouts: 'Breakouts',
      none: 'None of these'
    },
    feeling: {
      fine_tune: 'Pretty good, I just want to fine-tune',
      self_conscious: 'A bit self-conscious lately',
      frustrated: 'Frustrated, nothing’s really worked',
      not_my_skin: 'Honestly, it doesn’t feel like my skin anymore'
    },
    skin_type: {
      dry: 'Dry and tight, it drinks up moisture',
      oily: 'Oily or shiny by midday',
      combination: 'Combination: oily in some spots, dry in others',
      sensitive: 'Sensitive, it reacts easily',
      normal: 'Pretty normal and balanced'
    },
    routine: {
      quick: 'Keep it quick',
      few_minutes: 'A few minutes, morning and night',
      full_ritual: 'I love a full ritual'
    },
    experience: {
      nothing_worked: 'Yes, and nothing really worked',
      some_helped: 'Yes, some helped but not enough',
      first_time: 'No, this is my first time',
      tried_everything: 'I’ve tried everything at this point'
    },
    goal: {
      visible_results: 'Seeing visible results in the mirror',
      confident_bare: 'Feeling confident without makeup',
      routine_works: 'Finally having a routine that works',
      natural_safe: 'Knowing I’m using something natural & safe'
    }
  };

  // Question ID -> the property-name stem. The quiz identifier is prepended at
  // send time, so these read as "Find Your Ritual Age Range" in Klaviyo.
  var PROPERTY_STEMS = {
    age: 'Age Range',
    main_concern: 'Main Concern',
    secondary_concerns: 'Secondary Concerns',
    feeling: 'Skin Feeling',
    skin_type: 'Skin Type',
    routine: 'Routine Time',
    experience: 'Experience',
    goal: 'Goal'
  };

  // Q2 answer -> recommended bundle. This is the mapping documented on the Q2
  // step in sections/quiz-ritual.liquid.
  //
  // MUST STAY IN SYNC with the results screen when that is built. It is
  // duplicated here rather than deferred because the Klaviyo record is written
  // at email submit — before the results screen would ever run — and a lead
  // without a recommended bundle is close to useless for flow segmentation.
  var BUNDLE_FOR_CONCERN = {
    wrinkles_aging: 'mature_skin',
    dark_circles: 'mature_skin',
    dull_tone: 'mature_skin',
    acne_breakouts: 'acne',
    rosacea_redness: 'acne',
    eczema_dry: 'natural_pharmacy'
  };

  // Bundle key -> storefront product title. Mirrors the old funnel's map so the
  // two quizzes name the same product identically in Klaviyo.
  var BUNDLE_LABELS = {
    mature_skin: 'Complete System for Mature Women’s Skin',
    acne: 'Complete Acne System for Women',
    natural_pharmacy: 'Complete Natural Pharmacy for Face & Body'
  };

  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  // Loading interstitial. Same cadence as the /pages/quiz funnel: one beat per
  // photo, then a short hold on the last one before the results appear.
  var LOADING_PHOTO_INTERVAL = 1000;
  // Beats of the loading screen: bar fill 32% → 55% → 88%, one photo collage
  // per beat. quiz-ritual.css keys on data-loading-state.
  var LOADING_STATES = 3;
  var LOADING_TOTAL_MS = 3500;

  // Resolve one raw answer to its label, falling back to the raw value so a
  // copy change that outpaces the map degrades to the key rather than to blank.
  function labelFor(questionId, value) {
    var map = ANSWER_LABELS[questionId];
    if (!map) return value;
    return map[value] || value;
  }

  // GTM / GA4. Mirrors the old funnel so both quizzes report through one channel.
  function pushDataLayer(event, payload) {
    if (!window.dataLayer || typeof window.dataLayer.push !== 'function') return;
    var data = { event: event };
    if (payload) {
      Object.keys(payload).forEach(function (key) {
        data[key] = payload[key];
      });
    }
    window.dataLayer.push(data);
  }

  // Quiz events + order attribution. window.FroyaQuiz comes from
  // snippets/quiz-analytics.liquid (rendered before this script); without it
  // every call is a no-op. The legacy pushDataLayer events stay for GTM.
  function quizTrack(eventName, payload) {
    var quiz = window.FroyaQuiz;
    if (!quiz) return;
    quiz.track(eventName, Object.assign({ quiz_id: quiz.IDS.RITUAL }, payload || {}));
  }

  function QuizRitual(root) {
    this.root = root;
    this.storageKey = root.dataset.storageKey || 'froya:quiz:ritual:v1';

    this.steps = Array.prototype.slice.call(
      root.querySelectorAll('[data-quiz-step]')
    );
    this.progressFill = root.querySelector('[data-quiz-progress]');
    this.progressBar = root.querySelector('[data-quiz-progress-bar]');

    this.total = parseInt(root.dataset.stepsTotal, 10) || this.steps.length;
    // The bar runs one stop further than the counter — it only fills on the
    // email/results screen after the last question, not on the last question.
    this.progressTotal = parseInt(root.dataset.progressTotal, 10) || this.total + 1;
    this.index = 0;
    this.answers = {};
    // Lead identity for the email screen. Kept beside the answers rather than in
    // the form itself so a refresh mid-quiz restores what was already typed.
    this.identity = {};
    this.startedAt = Date.now();
    // Set once, in leaveForResults(), the moment the quiz hands off to the
    // results page. The results page refuses to render without it — a visitor
    // who opens that URL cold is sent back here. `index` cannot serve as the
    // flag: leaveForResults() rewinds it to the email step for the Back button.
    this.completedAt = null;
    this.advanceTimer = null;
    this.loadingTimer = null;
    this.loadingDoneTimer = null;

    this.quizName = (root.dataset.quizName || '').trim() || DEFAULT_QUIZ_NAME;
    this.emailStep = root.querySelector('[data-question-type="email"]');

    if (!this.steps.length) return;

    this.restore();
    this.bind();
    this.initEmail();
    this.render();

    // A refresh that resumes mid-quiz is not a new start.
    if (this.index === 0 && !Object.keys(this.answers).length) quizTrack('quiz_started');
  }

  /* ---------------------------------------------------------------- state -- */

  QuizRitual.prototype.restore = function () {
    var raw = null;
    try {
      raw = window.sessionStorage.getItem(this.storageKey);
    } catch (e) {
      /* private mode / storage disabled — run stateless */
    }
    if (!raw) return;

    var saved;
    try {
      saved = JSON.parse(raw);
    } catch (e) {
      return;
    }
    if (!saved || saved.v !== STORAGE_VERSION) return;

    this.answers = saved.answers && typeof saved.answers === 'object' ? saved.answers : {};
    this.identity = saved.identity && typeof saved.identity === 'object' ? saved.identity : {};
    // Preserve the original start time across a refresh so the reported
    // duration measures the whole quiz, not the time since the last reload.
    if (typeof saved.startedAt === 'number') this.startedAt = saved.startedAt;
    if (typeof saved.completedAt === 'number') this.completedAt = saved.completedAt;

    // Resume on the furthest step reached, clamped to a step that exists.
    var resume = parseInt(saved.index, 10);
    if (!isNaN(resume) && resume > 0 && resume < this.steps.length) {
      this.index = resume;
    }

    // Re-apply the saved selections to the buttons.
    var self = this;
    this.steps.forEach(function (step) {
      var value = self.answers[step.dataset.questionId];
      if (value === undefined || value === null) return;
      var wanted = Array.isArray(value) ? value : [value];
      self.optionsIn(step).forEach(function (button) {
        var on = wanted.indexOf(button.dataset.value) !== -1;
        button.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
    });
  };

  QuizRitual.prototype.persist = function () {
    try {
      window.sessionStorage.setItem(
        this.storageKey,
        JSON.stringify({
          v: STORAGE_VERSION,
          index: this.index,
          answers: this.answers,
          identity: this.identity,
          startedAt: this.startedAt,
          completedAt: this.completedAt
        })
      );
    } catch (e) {
      /* nothing we can do; the quiz still works for this pageview */
    }
  };

  /* --------------------------------------------------------------- events -- */

  QuizRitual.prototype.bind = function () {
    var self = this;

    this.root.addEventListener('click', function (event) {
      var option = event.target.closest('[data-quiz-option]');
      if (option && self.root.contains(option)) {
        self.onOption(option);
        return;
      }
      if (event.target.closest('[data-quiz-next]')) {
        self.next();
        return;
      }
      if (event.target.closest('[data-quiz-back]')) {
        self.back();
      }
    });

    // Let the device Back gesture walk back through the quiz. Step 0 is not
    // pushed, so Back from the first question leaves the page as normal.
    window.addEventListener('popstate', function (event) {
      var target = event.state && typeof event.state.quizRitualStep === 'number'
        ? event.state.quizRitualStep
        : 0;
      self.goTo(target, { history: false });
    });

    // Back/forward-cache restore (Back from the results page): the document
    // returns frozen exactly as it was left — loading step on screen, its
    // timers already spent — and a cross-document traversal fires pageshow,
    // not popstate. Re-render from the entry's state, which leaveForResults()
    // pointed at the email step. render() is called directly when the index
    // already matches, because goTo() treats a same-index call as a no-op.
    window.addEventListener('pageshow', function (event) {
      if (!event.persisted) return;
      var state = window.history.state;
      var target = state && typeof state.quizRitualStep === 'number'
        ? state.quizRitualStep
        : self.index;
      if (target === self.index) {
        self.render();
        return;
      }
      self.goTo(target, { history: false });
    });
  };

  /* ------------------------------------------------------------ selection -- */

  QuizRitual.prototype.optionsIn = function (step) {
    return Array.prototype.slice.call(step.querySelectorAll('[data-quiz-option]'));
  };

  QuizRitual.prototype.onOption = function (button) {
    var step = button.closest('[data-quiz-step]');
    if (!step) return;

    if (step.dataset.questionType === 'multi') {
      this.onMultiSelect(step, button);
    } else {
      this.onSingleSelect(step, button);
    }

    // Every step type can carry a nav, so the forward control is re-synced here
    // rather than inside each handler.
    this.syncNext(step);
  };

  QuizRitual.prototype.onSingleSelect = function (step, button) {
    this.optionsIn(step).forEach(function (other) {
      other.setAttribute('aria-pressed', other === button ? 'true' : 'false');
    });

    this.setAnswer(step.dataset.questionId, button.dataset.value);

    // Picking an answer IS the forward action on a single-select step, so every
    // one of them advances itself and none renders a forward control.
    // data-auto-advance="false" opts a step out if one ever needs to hold.
    if (step.dataset.autoAdvance !== 'false') {
      var self = this;
      window.clearTimeout(this.advanceTimer);
      // A short beat so the selected state is actually seen before the step
      // changes. Not a race workaround — the advance does not depend on it.
      this.advanceTimer = window.setTimeout(function () {
        self.goTo(self.index + 1);
      }, AUTO_ADVANCE_DELAY);
    }
  };

  QuizRitual.prototype.onMultiSelect = function (step, button) {
    var options = this.optionsIn(step);
    var max = parseInt(step.dataset.max, 10) || options.length;
    var isMutex = button.hasAttribute('data-quiz-mutex');
    var turningOn = button.getAttribute('aria-pressed') !== 'true';

    if (isMutex) {
      // "None of these" clears everything else.
      options.forEach(function (other) {
        other.setAttribute('aria-pressed', other === button && turningOn ? 'true' : 'false');
      });
    } else {
      if (turningOn) {
        var selected = options.filter(function (other) {
          return other.getAttribute('aria-pressed') === 'true' && !other.hasAttribute('data-quiz-mutex');
        });
        if (selected.length >= max) {
          this.showError(step, step.getAttribute('data-error-max'));
          return;
        }
      }
      button.setAttribute('aria-pressed', turningOn ? 'true' : 'false');
      // Any real concern clears the mutually-exclusive "none" option.
      options.forEach(function (other) {
        if (other.hasAttribute('data-quiz-mutex')) other.setAttribute('aria-pressed', 'false');
      });
    }

    this.hideError(step);

    // Commit in TAP order, not DOM order: the results page renders the Q3
    // bullets "in the order picked" (copy-logic doc §3). The DOM decides what is
    // selected; the previously saved answer decides the order of anything still
    // selected, and whatever is newly pressed goes on the end.
    var pressed = this.optionsIn(step)
      .filter(function (other) { return other.getAttribute('aria-pressed') === 'true'; })
      .map(function (other) { return other.dataset.value; });

    var prev = this.answers[step.dataset.questionId];
    if (typeof prev === 'string') prev = [prev];
    if (Object.prototype.toString.call(prev) !== '[object Array]') prev = [];

    var values = prev.filter(function (value) { return pressed.indexOf(value) !== -1; });
    pressed.forEach(function (value) {
      if (values.indexOf(value) === -1) values.push(value);
    });

    this.setAnswer(step.dataset.questionId, values);
    // No auto-advance here, deliberately, and not even for the mutually
    // exclusive "none of these": a multi-select step cannot know the user is
    // finished choosing, so only the "continue" button moves it on.
  };

  QuizRitual.prototype.setAnswer = function (questionId, value) {
    if (!questionId) return;
    this.answers[questionId] = value;
    this.persist();
    this.emit('quiz-ritual:answer', {
      questionId: questionId,
      value: value,
      answers: this.getAnswers()
    });
  };

  /* ------------------------------------------------------------ navigation -- */

  QuizRitual.prototype.next = function () {
    var step = this.steps[this.index];
    if (!step) return;

    if (step.dataset.questionType === 'multi') {
      var chosen = this.optionsIn(step).filter(function (other) {
        return other.getAttribute('aria-pressed') === 'true';
      });
      if (!chosen.length) {
        this.showError(step, step.getAttribute('data-error-min'));
        return;
      }
    }
    this.goTo(this.index + 1);
  };

  QuizRitual.prototype.back = function () {
    this.goTo(this.index - 1);
  };

  QuizRitual.prototype.goTo = function (index, options) {
    options = options || {};
    if (index < 0 || index >= this.steps.length || index === this.index) return;

    // quiz_step: a forward move off a question step commits its answer.
    // History traversal (popstate / bfcache) is a revisit, not a commit.
    if (index > this.index && options.history !== false) this.trackStepCommit(this.steps[this.index]);

    window.clearTimeout(this.advanceTimer);
    this.index = index;
    this.persist();
    this.render();

    if (options.history !== false) {
      try {
        window.history.pushState({ quizRitualStep: index }, '');
      } catch (e) {
        /* history unavailable — navigation still works */
      }
    }

    // Bring the top of the quiz into view; the step above may have been taller.
    window.scrollTo({ top: 0, behavior: 'auto' });

    if (index === this.steps.length - 1) {
      this.emit('quiz-ritual:complete', { answers: this.getAnswers() });
    }
  };

  // One event per committed question, so drop-off shows at which one. The
  // email step carries no data-question-id and is reported by
  // quiz_email_captured instead.
  QuizRitual.prototype.trackStepCommit = function (step) {
    var questionId = step && step.dataset.questionId;
    if (!questionId) return;
    var answer = this.answers[questionId];
    var declared = parseInt(step.dataset.stepNumber, 10);
    quizTrack('quiz_step', {
      step_index: !isNaN(declared) ? declared : this.index + 1,
      question_id: questionId,
      answer: answer === undefined ? null : answer
    });
  };

  /* ---------------------------------------------------------------- render -- */

  QuizRitual.prototype.render = function () {
    var self = this;

    this.steps.forEach(function (step, i) {
      var active = i === self.index;
      step.classList.toggle('is-active', active);
      step.setAttribute('aria-hidden', active ? 'false' : 'true');
      if (active) self.syncNext(step);
    });

    // Warm the next step's artwork so advancing does not pop. Only ever one
    // step ahead — fetching all eight up front would waste most of them.
    this.warmStep(this.index + 1);

    // The loading interstitial runs itself. Driven from render() rather than
    // from goTo() so that a refresh which restores onto this step starts the
    // sequence too, and so leaving by ANY route — back gesture included —
    // cancels the timers rather than firing a redirect from a step the visitor
    // has already left.
    if (self.steps[self.index] &&
        self.steps[self.index].dataset.questionType === 'loading') {
      self.runLoading();
    } else {
      self.clearLoading();
    }

    var stepNumber = this.stepNumber();
    var percent = Math.min(100, (stepNumber / this.progressTotal) * 100);

    if (this.progressFill) this.progressFill.style.width = percent + '%';
    if (this.progressBar) this.progressBar.setAttribute('aria-valuenow', String(stepNumber));

    this.emit('quiz-ritual:step', {
      index: this.index,
      stepNumber: stepNumber,
      total: this.total
    });
  };

  /**
   * Prefetch one step's decorative frame.
   *
   * The frame lives in a display:none step, so the browser will not load it on
   * its own — that is the point, but it means advancing would otherwise start
   * the download from cold. Fetching through a detached Image with the same
   * srcset/sizes lets the browser pick exactly the candidate it will later
   * need, and the response is served from cache when the step appears.
   */
  QuizRitual.prototype.warmStep = function (index) {
    var step = this.steps[index];
    if (!step) return;

    // A step may carry several decor <picture>s (art + note card); warm each.
    var pictures = step.querySelectorAll('picture');
    if (pictures.length > 1) {
      for (var p = 0; p < pictures.length; p++) this.warmPicture(pictures[p]);
      return;
    }
    var picture = pictures[0];
    if (!picture) {
      // No <picture> means a step whose artwork is plain <img> — the loading
      // interstitial's three photos. They are lazy AND inside a display:none
      // step, so nothing would fetch them until the step appeared and the first
      // beat would show an empty box. Warming them one step early (i.e. while
      // the visitor is filling in the email form) is what avoids that.
      this.warmImages(step);
      return;
    }
    this.warmPicture(picture);
  };

  /** Prefetch one <picture>, mirroring the browser's <source> choice. */
  QuizRitual.prototype.warmPicture = function (picture) {
    if (picture.dataset.warmed === 'true') return;

    var img = picture.querySelector('img');
    if (!img) return;
    picture.dataset.warmed = 'true';

    // Mirror the browser's own <source> selection for this viewport.
    var candidate = img;
    var sources = picture.querySelectorAll('source');
    for (var i = 0; i < sources.length; i++) {
      var media = sources[i].getAttribute('media');
      if (!media || (window.matchMedia && window.matchMedia(media).matches)) {
        candidate = sources[i];
        break;
      }
    }

    var pre = new Image();
    if (pre.fetchPriority !== undefined) pre.fetchPriority = 'low';
    pre.sizes = candidate.getAttribute('sizes') || '100vw';
    var srcset = candidate.getAttribute('srcset');
    if (srcset) pre.srcset = srcset;
    else pre.src = img.currentSrc || img.src;
  };

  /**
   * Prefetch every plain <img> in a step, honouring its own srcset/sizes so the
   * browser picks the exact candidate it will later render and serves it from
   * cache. Idempotent — a step is only ever warmed once.
   */
  QuizRitual.prototype.warmImages = function (step) {
    if (step.dataset.warmed === 'true') return;
    var images = step.querySelectorAll('img[srcset], img[src]');
    if (!images.length) return;
    step.dataset.warmed = 'true';

    for (var i = 0; i < images.length; i++) {
      var pre = new Image();
      if (pre.fetchPriority !== undefined) pre.fetchPriority = 'low';
      pre.sizes = images[i].getAttribute('sizes') || '100vw';
      var srcset = images[i].getAttribute('srcset');
      if (srcset) pre.srcset = srcset;
      else pre.src = images[i].getAttribute('src');
    }
  };

  // The step's own number wins, so non-question steps (email, loading) can sit
  // in the same list without throwing the "n/8" counter off.
  QuizRitual.prototype.stepNumber = function () {
    var step = this.steps[this.index];
    var declared = step && parseInt(step.dataset.stepNumber, 10);
    return !isNaN(declared) && declared > 0 ? declared : this.index + 1;
  };

  QuizRitual.prototype.syncNext = function (step) {
    var button = step.querySelector('[data-quiz-next]');
    if (!button) return;
    var chosen = this.optionsIn(step).filter(function (other) {
      return other.getAttribute('aria-pressed') === 'true';
    });
    button.disabled = chosen.length === 0;
  };

  // Messages come from the step's data-error-min / data-error-max, which
  // sections/quiz-ritual.liquid fills from sections.quiz_ritual.* locale keys
  // so they translate. A missing one leaves the server-rendered text as is.
  QuizRitual.prototype.showError = function (step, message) {
    var el = step.querySelector('[data-quiz-error]');
    if (!el) return;
    if (message) el.textContent = message;
    el.hidden = false;
  };

  QuizRitual.prototype.hideError = function (step) {
    var el = step.querySelector('[data-quiz-error]');
    if (el) el.hidden = true;
  };

  /* ------------------------------------------------------------------ api -- */

  QuizRitual.prototype.getAnswers = function () {
    return JSON.parse(JSON.stringify(this.answers));
  };

  QuizRitual.prototype.getIdentity = function () {
    return JSON.parse(JSON.stringify(this.identity || {}));
  };

  QuizRitual.prototype.isComplete = function () {
    var self = this;
    return this.steps.every(function (step) {
      if (!step.dataset.questionId) return true;
      var value = self.answers[step.dataset.questionId];
      if (Array.isArray(value)) return value.length > 0;
      return value !== undefined && value !== null && value !== '';
    });
  };

  QuizRitual.prototype.emit = function (name, detail) {
    document.dispatchEvent(new CustomEvent(name, { detail: detail, bubbles: true }));
  };

  /* ---------------------------------------------------------- email step -- */
  /**
   * The screen after Q8. It is an ordinary step as far as the engine is
   * concerned — same show/hide, same progress bar, same back button — so all of
   * this hangs off the form and nothing here can affect a question step.
   *
   * The step carries no data-question-id, so isComplete() never counts it as an
   * unanswered question.
   */

  QuizRitual.prototype.initEmail = function () {
    var step = this.emailStep;
    if (!step) return;

    this.emailForm = step.querySelector('[data-quiz-email-form]');
    if (!this.emailForm) return;

    this.firstNameField = this.emailForm.querySelector('[data-quiz-first-name]');
    this.emailField = this.emailForm.querySelector('[data-quiz-email-field]');
    this.consentField = this.emailForm.querySelector('[data-quiz-consent]');
    this.submitButton = this.emailForm.querySelector('[data-quiz-email-submit]');
    this.emailError = this.emailForm.querySelector('[data-quiz-email-error]');

    var self = this;

    this.emailForm.addEventListener('submit', function (event) {
      event.preventDefault();
      self.submitEmail();
    });

    // One delegated listener for both text inputs and the checkbox, so the CTA
    // unlocks the moment the last of the three is satisfied.
    this.emailForm.addEventListener('input', function () {
      self.syncSubmit();
    });
    this.emailForm.addEventListener('change', function () {
      self.syncSubmit();
    });

    // Validate on blur, never while typing: flagging "a@" as invalid halfway
    // through the address is noise, not help.
    if (this.emailField) {
      this.emailField.addEventListener('blur', function () {
        if (self.emailField.value.trim()) self.validateEmail();
      });
    }

    var skip = step.querySelector('[data-quiz-skip]');
    if (skip) {
      skip.addEventListener('click', function () {
        self.skipEmail();
      });
    }

    this.restoreIdentity();
    this.syncSubmit();
  };

  // Put back whatever was typed before a refresh. Consent is deliberately NOT
  // restored as checked unless it genuinely was — an opt-in has to be a fresh,
  // deliberate act, and re-ticking it for the visitor would fake one.
  QuizRitual.prototype.restoreIdentity = function () {
    var identity = this.identity || {};
    if (this.firstNameField && identity.first_name) {
      this.firstNameField.value = identity.first_name;
    }
    if (this.emailField && identity.email) {
      this.emailField.value = identity.email;
    }
    if (this.consentField && identity.marketing_consent === true) {
      this.consentField.checked = true;
    }
  };

  QuizRitual.prototype.syncSubmit = function () {
    if (!this.submitButton) return;
    var name = this.firstNameField ? this.firstNameField.value.trim() : '';
    var email = this.emailField ? this.emailField.value.trim() : '';
    var consented = this.consentField ? this.consentField.checked : true;
    this.submitButton.disabled = !(name && email && consented);
  };

  QuizRitual.prototype.validateEmail = function () {
    if (!this.emailField) return true;
    var ok = EMAIL_RE.test(this.emailField.value.trim());
    this.emailField.classList.toggle('is-invalid', !ok);
    this.emailField.setAttribute('aria-invalid', ok ? 'false' : 'true');
    if (this.emailError) this.emailError.hidden = ok;
    return ok;
  };

  QuizRitual.prototype.submitEmail = function () {
    var name = this.firstNameField ? this.firstNameField.value.trim() : '';
    if (!name) {
      if (this.firstNameField) this.firstNameField.focus();
      return;
    }
    if (!this.validateEmail()) {
      this.emailField.focus();
      return;
    }

    var consent = this.consentField ? this.consentField.checked : false;

    this.identity = {
      first_name: name,
      email: this.emailField.value.trim(),
      marketing_consent: consent,
      skipped: false
    };
    this.persist();

    // The CTA is only enabled with consent ticked, so this always passes — it
    // is here so a future change to that gate cannot leak an un-consented lead.
    if (consent) {
      var properties = this.collectProperties();
      this.subscribeToKlaviyo(name, this.identity.email, properties);
      this.trackKlaviyoEvent(name, this.identity.email, properties);
    }

    pushDataLayer('quiz_email_submit', {
      quiz_name: this.quizName,
      quiz_marketing_consent: consent
    });
    quizTrack('quiz_email_captured', { subscribed: true });

    this.emit('quiz-ritual:email', {
      identity: JSON.parse(JSON.stringify(this.identity)),
      answers: this.getAnswers()
    });

    this.finishEmail();
  };

  QuizRitual.prototype.skipEmail = function () {
    this.identity = { skipped: true };
    this.persist();

    pushDataLayer('quiz_email_skip', { quiz_name: this.quizName });
    quizTrack('quiz_email_captured', { subscribed: false });
    this.emit('quiz-ritual:email', { identity: { skipped: true }, answers: this.getAnswers() });

    this.finishEmail();
  };

  /**
   * Where the quiz goes once the lead is captured: through the loading
   * interstitial, then on to the results.
   *
   * The interstitial is SKIPPED when there is nowhere for it to lead. A loading
   * screen that never resolves is worse than no loading screen, so with no
   * Results page URL and no results step the visitor stays on the email screen
   * in its submitted state instead of watching photos cycle forever.
   */
  QuizRitual.prototype.finishEmail = function () {
    var loading = this.stepIndexByType('loading');
    if (loading !== -1 && loading !== this.index && this.hasResultsDestination()) {
      this.goTo(loading);
      return;
    }
    this.goToResults();
  };

  QuizRitual.prototype.hasResultsDestination = function () {
    if ((this.root.dataset.resultsUrl || '').trim()) return true;
    return this.stepIndexByType('results') !== -1;
  };

  /**
   * The results screen is a separate build. Until it exists, set the section's
   * "Results page" setting and this redirects there; add a step with
   * data-question-type="results" later and it advances to that instead, with no
   * change here.
   *
   * With neither in place the lead is still captured and reported — the visitor
   * simply stays put, which is why the CTA is left disabled rather than bouncing
   * back to an enabled state that invites a second submit.
   */
  QuizRitual.prototype.goToResults = function () {
    var url = (this.root.dataset.resultsUrl || '').trim();
    if (url) {
      this.leaveForResults();
      window.location.href = url;
      return;
    }
    var results = this.stepIndexByType('results');
    if (results !== -1) {
      this.goTo(results);
      return;
    }
    if (this.submitButton) this.submitButton.disabled = true;
    if (this.emailStep) this.emailStep.setAttribute('data-quiz-email-state', 'submitted');
  };

  /**
   * Browser Back from the results page must land on the EMAIL step, never the
   * loading interstitial. The last history entry pushed before leaving is the
   * loading step's, so without this Back returned to a screen that either
   * re-ran the interstitial and bounced straight back to the results, or sat
   * frozen. Both places the return trip reads the step from are rewritten:
   *   - a fresh load resumes from the persisted index (restore()), so that is
   *     moved to the email step in sessionStorage;
   *   - a back/forward-cache restore keeps the frozen DOM and fires pageshow,
   *     not popstate, so the current history entry's state is replaced and the
   *     pageshow handler in bind() re-renders from it.
   * Nothing is rendered here: the loading screen stays up until the results
   * page takes over. Deliberately NOT fixed by re-running the loading timer.
   */
  QuizRitual.prototype.leaveForResults = function () {
    var email = this.stepIndexByType('email');
    if (email === -1) return;
    this.index = email;
    // The completion flag the results page gates on (quiz-results-header.liquid).
    // Only reachable through finishEmail() -> loading -> goToResults(), i.e.
    // after every question and the email step, so "finished" means finished.
    this.completedAt = Date.now();
    this.persist();
    try {
      window.history.replaceState({ quizRitualStep: email }, '', window.location.href);
    } catch (e) {
      /* history unavailable — the persisted index still covers a fresh load */
    }
  };

  QuizRitual.prototype.stepIndexByType = function (type) {
    for (var i = 0; i < this.steps.length; i++) {
      if (this.steps[i].dataset.questionType === type) return i;
    }
    return -1;
  };

  /* ------------------------------------------------------ loading screen -- */
  /**
   * Step the loading beats, then hand off to the results.
   *
   * The copy is static here — the /pages/quiz funnel rotates its text over a
   * fixed photo. The animation is the step's data-loading-state (0, 1, 2),
   * which quiz-ritual.css turns into the bar fill width and which of the three
   * photo collages is visible. Everything is already in the DOM and sized up front, so
   * a beat costs one attribute write and no layout.
   */
  QuizRitual.prototype.runLoading = function () {
    var step = this.steps[this.index];
    if (!step || this.loadingTimer) return;

    var self = this;
    var state = 0;

    step.setAttribute('data-loading-state', '0');

    this.loadingTimer = window.setInterval(function () {
      state += 1;
      // Hold on the last beat rather than looping back to the first — the
      // sequence reads as progress, and restarting it would read as a stall.
      if (state >= LOADING_STATES) {
        window.clearInterval(self.loadingTimer);
        self.loadingTimer = null;
        return;
      }
      step.setAttribute('data-loading-state', String(state));
    }, LOADING_PHOTO_INTERVAL);

    this.loadingDoneTimer = window.setTimeout(function () {
      self.loadingDoneTimer = null;
      self.goToResults();
    }, LOADING_TOTAL_MS);
  };

  QuizRitual.prototype.clearLoading = function () {
    if (this.loadingTimer) {
      window.clearInterval(this.loadingTimer);
      this.loadingTimer = null;
    }
    if (this.loadingDoneTimer) {
      window.clearTimeout(this.loadingDoneTimer);
      this.loadingDoneTimer = null;
    }
  };

  /* ------------------------------------------------------------- reporting -- */

  // Q2's answer decides the bundle; see BUNDLE_FOR_CONCERN.
  QuizRitual.prototype.recommendedBundle = function () {
    return BUNDLE_FOR_CONCERN[this.answers.main_concern] || null;
  };

  // The flat {property: value} object shared by the Klaviyo profile properties
  // and the Klaviyo event. Every name is prefixed with the quiz identifier.
  QuizRitual.prototype.collectProperties = function () {
    var props = {};
    var prefix = this.quizName + ' ';
    var self = this;

    Object.keys(PROPERTY_STEMS).forEach(function (questionId) {
      var name = prefix + PROPERTY_STEMS[questionId];
      var raw = self.answers[questionId];
      if (raw === undefined || raw === null) return;

      if (Array.isArray(raw)) {
        props[name] = raw;
        props[name + ' Label'] = raw.map(function (value) {
          return labelFor(questionId, value);
        });
      } else {
        props[name] = raw;
        props[name + ' Label'] = labelFor(questionId, raw);
      }
    });

    var bundle = this.recommendedBundle();
    if (bundle) {
      props[prefix + 'Recommended Bundle'] = bundle;
      props[prefix + 'Recommended Bundle Label'] = BUNDLE_LABELS[bundle] || bundle;
    }

    props[prefix + 'Duration Seconds'] = Math.round((Date.now() - this.startedAt) / 1000);
    props['Quiz Name'] = this.quizName;

    return props;
  };

  // Single consent gate for every outbound call. Consentmo sets __kla_off=true
  // in theme.liquid when the visitor declines marketing.
  QuizRitual.prototype.klaviyoAllowed = function () {
    return !/(?:^|;\s*)__kla_off=true/.test(document.cookie);
  };

  /**
   * Subscribe the lead via Klaviyo's client-side Subscribe API.
   *
   * Profile properties always hold the LATEST answers — a retake overwrites
   * them. The event below is what preserves history.
   *
   * Fire-and-forget with keepalive, and every failure is swallowed: a reporting
   * problem must never be able to block the visitor from reaching their results.
   */
  QuizRitual.prototype.subscribeToKlaviyo = function (firstName, email, quizProperties) {
    var companyId = this.root.dataset.klaviyoCompanyId;
    var listId = this.root.dataset.klaviyoListId;
    if (!companyId || !listId || !email) return;
    if (!this.klaviyoAllowed()) return;

    var source = this.quizName + ' Quiz';
    var properties = { 'Lead Source': source, 'Quiz Lead': true };
    Object.keys(quizProperties || {}).forEach(function (key) {
      properties[key] = quizProperties[key];
    });

    var payload = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: source,
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email: email,
                first_name: firstName,
                properties: properties
              }
            }
          }
        },
        relationships: {
          list: { data: { type: 'list', id: listId } }
        }
      }
    };

    this.postToKlaviyo('subscriptions', companyId, payload);
  };

  /**
   * Timestamped record of the completed quiz. Unlike profile properties this
   * accumulates, so retakes are preserved and flows can trigger on the metric.
   * Klaviyo auto-creates the metric on first receipt — nothing to set up in the UI.
   */
  QuizRitual.prototype.trackKlaviyoEvent = function (firstName, email, quizProperties) {
    var companyId = this.root.dataset.klaviyoCompanyId;
    if (!companyId || !email) return;
    if (!this.klaviyoAllowed()) return;

    var source = this.quizName + ' Quiz';
    var properties = { 'Lead Source': source };
    Object.keys(quizProperties || {}).forEach(function (key) {
      properties[key] = quizProperties[key];
    });

    var payload = {
      data: {
        type: 'event',
        attributes: {
          properties: properties,
          metric: {
            data: {
              type: 'metric',
              attributes: { name: 'Completed ' + source }
            }
          },
          profile: {
            data: {
              type: 'profile',
              attributes: { email: email, first_name: firstName }
            }
          }
        }
      }
    };

    this.postToKlaviyo('events', companyId, payload);
  };

  QuizRitual.prototype.postToKlaviyo = function (endpoint, companyId, payload) {
    try {
      window.fetch(
        'https://a.klaviyo.com/client/' + endpoint + '/?company_id=' + encodeURIComponent(companyId),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            revision: KLAVIYO_REVISION
          },
          body: JSON.stringify(payload),
          // The submit usually navigates away immediately; keepalive is what
          // stops the browser cancelling the request as the page unloads.
          keepalive: true
        }
      ).catch(function () {});
    } catch (e) {
      /* never let a tracking failure break the quiz */
    }
  };

  /* -------------------------------------------------------- reviews slider -- */
  /**
   * Q1's three-card review carousel. Independent of the step engine — it owns
   * only the cards inside [data-quiz-reviews] — so it cannot affect answers,
   * progress or navigation.
   *
   * Reader-driven: it never moves on its own. Swipe/drag it, tap a side card to
   * pull it in, or use the dots.
   *
   * Moving never touches the DOM: the selected card is always the middle child,
   * so advancing is a matter of rewriting each card's CSS `order`. The scale and
   * fade are handled entirely by the stylesheet.
   *
   * Deliberately not a native overflow-x scroller. The selected card is larger
   * than its neighbours, so whichever card is centred changes the row's layout —
   * under scroll-snap that feeds back into the scroll position and the centre
   * card can oscillate. Discrete steps have no such loop.
   */

  var SWIPE_THRESHOLD = 30; // px of travel before a drag counts as a swipe

  function ReviewsSlider(root) {
    this.root = root;
    this.cards = Array.prototype.slice.call(root.querySelectorAll('[data-quiz-review]'));
    this.dots = Array.prototype.slice.call(root.querySelectorAll('[data-quiz-review-dot]'));
    this.count = this.cards.length;
    this.index = 0;
    this.pointerX = null;
    this.lastX = null;

    if (this.count < 2) return;
    this.bind();
  }

  ReviewsSlider.prototype.show = function (index) {
    var n = this.count;
    // Which slot the selected card occupies: dead centre of an odd row.
    var middle = Math.floor(n / 2);
    this.index = ((index % n) + n) % n;

    var self = this;
    this.cards.forEach(function (card, i) {
      card.style.order = String((i - self.index + middle + n) % n);
      card.classList.toggle('is-current', i === self.index);
    });
    this.dots.forEach(function (dot, i) {
      dot.classList.toggle('is-current', i === self.index);
    });
  };

  ReviewsSlider.prototype.bind = function () {
    var self = this;

    this.root.addEventListener('click', function (event) {
      var dot = event.target.closest('[data-quiz-review-dot]');
      if (dot) {
        self.show(parseInt(dot.dataset.quizReviewDot, 10) || 0);
        return;
      }
      // Tapping a flanking card pulls it into the centre. Guarded on the drag
      // distance so the click that ends a swipe cannot also count as a tap.
      if (self.dragged) return;
      var card = event.target.closest('[data-quiz-review]');
      if (card) self.show(self.cards.indexOf(card));
    });

    // Drag/swipe. Passive throughout — the row never needs to block scrolling,
    // so a horizontal drag is read without costing vertical scroll latency.
    //
    // Three things make this work on EVERY device, not just a mouse:
    //  1. The track has `touch-action: pan-y` (quiz-ritual.css). Without it a
    //     mobile browser treats any touch pan as a page scroll, takes the
    //     gesture and fires `pointercancel` — the swipe never reached us.
    //  2. Pointer capture: the finger/mouse routinely leaves the 89px-tall row
    //     before lifting, and without capture `pointerup` is delivered to
    //     whatever is under it, not to this root.
    //  3. `dragstart` is suppressed: a mouse drag that starts on a card's <img>
    //     otherwise becomes a native image drag and no `pointerup` ever fires.
    // `pointermove` tracks the last position so that even a `pointercancel`
    // (older browsers still emit one mid-gesture) can settle the swipe.
    function settle(clientX) {
      if (self.pointerX === null) return;
      var dx = clientX - self.pointerX;
      self.pointerX = null;
      self.lastX = null;
      self.dragged = Math.abs(dx) >= SWIPE_THRESHOLD;
      if (self.dragged) self.show(self.index + (dx < 0 ? 1 : -1));
    }

    this.root.addEventListener('pointerdown', function (event) {
      if (event.isPrimary === false) return;
      if (typeof event.button === 'number' && event.button !== 0) return;
      // A mouse press is the start of a drag, never of a text selection: the
      // default action of pointerdown is where the browser begins one, and
      // user-select alone does not stop a selection that is extended INTO the
      // row from text outside it. Mouse only, so touch keeps its passive path
      // (blocking touch here cannot stop scrolling anyway; touch-action does).
      // Taps still work — click is unaffected by cancelling pointerdown.
      if (event.pointerType === 'mouse') event.preventDefault();
      self.pointerX = event.clientX;
      self.lastX = event.clientX;
      self.dragged = false;
      try { self.root.setPointerCapture(event.pointerId); } catch (e) { /* not supported */ }
    }, { passive: false });

    this.root.addEventListener('pointermove', function (event) {
      if (self.pointerX === null) return;
      self.lastX = event.clientX;
    }, { passive: true });

    this.root.addEventListener('pointerup', function (event) {
      settle(event.clientX);
    }, { passive: true });

    this.root.addEventListener('pointercancel', function () {
      // Settle from the last known position instead of throwing the gesture
      // away — with touch-action set this is rare, but harmless when it fires.
      if (self.lastX !== null && self.lastX !== undefined) settle(self.lastX);
      else { self.pointerX = null; self.dragged = false; }
    }, { passive: true });

    this.root.addEventListener('dragstart', function (event) {
      event.preventDefault();
    });
  };

  /* ------------------------------------------------------------ chat off -- */
  /**
   * Remove the Mimir chat launcher for the whole quiz.
   *
   * REMOVED, not hidden, and that is not a shortcut — it is the only thing that
   * works. The widget's host is #mimir-bubble-v2-root, a body-level div whose
   * launcher lives in an open shadow root. Its shadow stylesheet carries an
   * !important display rule on :host, and for !important declarations the
   * cascade order REVERSES: shadow-tree styles beat the document. Measured in
   * the browser — with `display: none !important` sitting in the element's own
   * inline style attribute, computed display was still `block`. So no
   * stylesheet rule and no inline style can hide this from the page.
   *
   * Taking the node out of the DOM sidesteps the cascade entirely. The observer
   * handles remounts, since the app recreates the root after teardown.
   */
  var CHAT_ROOTS = '#mimir-bubble-v2-root, #mimirContainer, .right-bubble-right';

  function hideChat() {
    var i;
    var known = document.querySelectorAll(CHAT_ROOTS);
    for (i = 0; i < known.length; i++) known[i].remove();

    // Fallback if the app renames its root again: find whoever is hosting a
    // shadow tree with the launcher in it, and drop that.
    var tops = document.body ? document.body.children : [];
    for (i = tops.length - 1; i >= 0; i--) {
      var el = tops[i];
      if (!el.shadowRoot) continue;
      try {
        if (el.shadowRoot.querySelector('.launcher[data-side], .launcher-button')) el.remove();
      } catch (e) {
        /* closed shadow root — unreachable from here */
      }
    }
  }

  function watchChat() {
    hideChat();
    if (!window.MutationObserver || !document.body) return;
    new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        if (records[i].addedNodes.length) { hideChat(); return; }
      }
    }).observe(document.body, { childList: true, subtree: true });
  }

  /* ----------------------------------------------------------------- boot -- */

  function initReviews(root) {
    var wrap = root.querySelector('[data-quiz-reviews]');
    if (!wrap) return null;
    return new ReviewsSlider(wrap);
  }

  function boot() {
    var root = document.querySelector('[data-quiz-ritual]');
    if (!root || root.dataset.quizRitualReady === 'true') return;
    root.dataset.quizRitualReady = 'true';
    window.FroyaRitualQuiz = new QuizRitual(root);
    window.FroyaRitualQuiz.reviews = initReviews(root);
    watchChat();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Theme editor re-renders the section without a page load.
  document.addEventListener('shopify:section:load', function (event) {
    if (event.target && event.target.querySelector('[data-quiz-ritual]')) {
      var root = event.target.querySelector('[data-quiz-ritual]');
      root.dataset.quizRitualReady = 'true';
      window.FroyaRitualQuiz = new QuizRitual(root);
      window.FroyaRitualQuiz.reviews = initReviews(root);
    }
  });
})();
